// LoyaltyWallOfFame — top 10 returning guests widget for Owner BI.
import React, { useCallback, useEffect, useState } from 'react';
import { Crown, Loader2, RefreshCw, Trophy, X, Home, TrendingUp, Bed } from 'lucide-react';
import { apiCall } from './_shared';

const eur = (n) => `€${Math.round(Number(n || 0)).toLocaleString('en-EU')}`;

const flag = (cc) => {
  if (!cc || cc.length !== 2) return '🏳️';
  const A = 0x1F1E6;
  return String.fromCodePoint(...cc.toUpperCase().split('').map(c => A + (c.charCodeAt(0) - 65)));
};

const LoyaltyWallOfFame = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const json = await apiCall('/api/admin/bi/loyalty-hall-of-fame?limit=10');
      setData(json);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (guest) => {
    setDetail({ guest, data: null });
    setDetailLoading(true);
    try {
      const params = new URLSearchParams();
      if (guest.email) params.append('email', guest.email);
      else if (guest.name) params.append('name', guest.name);
      const json = await apiCall(`/api/admin/bi/loyalty-guest-detail?${params}`);
      setDetail({ guest, data: json });
    } catch (e) {
      setDetail({ guest, error: e.message });
    } finally { setDetailLoading(false); }
  };

  const items = data?.items || [];

  // Rows hoisted OUT of JSX (Babel workaround).
  const rows = items.map((g, i) => {
    const trophyColor = i === 0 ? 'text-yellow-400' : i === 1 ? 'text-slate-300' : i === 2 ? 'text-amber-600' : 'text-gray-300';
    return (
      <tr key={(g.email || g.name) + i} onClick={() => openDetail(g)} className="border-b last:border-b-0 hover:bg-orange-50/40 cursor-pointer" data-testid={`loyalty-row-${i}`}>
        <td className="py-2 pl-3 pr-3">
          <div className="flex items-center gap-2">
            <Trophy className={`w-4 h-4 ${trophyColor}`} />
            <span className="text-sm font-bold text-gray-500 w-5">#{i + 1}</span>
          </div>
        </td>
        <td className="py-2 pr-3">
          <p className="font-semibold text-gray-900 text-sm truncate max-w-[200px]">{g.name || '—'}</p>
          {g.email && <p className="text-[11px] text-gray-500 truncate max-w-[200px]">{g.email}</p>}
        </td>
        <td className="py-2 pr-3 text-lg">{flag(g.country)}</td>
        <td className="py-2 pr-3 text-sm text-right font-bold text-orange-600">{g.visits}</td>
        <td className="py-2 pr-3 text-sm text-right text-gray-700">{g.nights}n</td>
        <td className="py-2 pr-3 text-sm text-right font-semibold text-green-600">{eur(g.revenue)}</td>
      </tr>
    );
  });

  return (
    <div className="bg-white rounded-xl border p-5" data-testid="loyalty-wall">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" /> Loyalty Wall of Fame
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Top returning guests (2+ visits) over the last 24 months
            {data && ` · ${data.count_2plus} total loyal guests`}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs text-gray-500 hover:text-gray-700"
          data-testid="loyalty-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      {err ? (
        <p className="text-sm text-red-600">{err}</p>
      ) : loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">No returning guests yet — every new arrival is a first-timer.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left py-2 pl-3 pr-3 w-16">Rank</th>
                <th className="text-left py-2 pr-3">Guest</th>
                <th className="text-left py-2 pr-3">Country</th>
                <th className="text-right py-2 pr-3">Visits</th>
                <th className="text-right py-2 pr-3">Nights</th>
                <th className="text-right py-2 pr-3">Revenue</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
      )}

      {/* Guest detail drawer */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setDetail(null)} data-testid="loyalty-drawer">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-rose-600 text-white p-5 flex justify-between items-start">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest opacity-90">Loyal guest</p>
                <h3 className="text-lg font-bold truncate">{detail.guest.name || detail.guest.email}</h3>
                {detail.guest.email && detail.guest.name && <p className="text-xs opacity-90 truncate">{detail.guest.email}</p>}
              </div>
              <button onClick={() => setDetail(null)} className="p-1 hover:bg-white/20 rounded" data-testid="loyalty-drawer-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center p-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
            ) : detail.error ? (
              <p className="p-5 text-sm text-red-600">{detail.error}</p>
            ) : detail.data ? (
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-orange-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold">Bookings</p>
                    <p className="text-2xl font-bold text-orange-600">{detail.data.total_bookings}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-green-700 font-semibold">Total revenue</p>
                    <p className="text-2xl font-bold text-green-600">{eur(detail.data.total_revenue)}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Nights</p>
                    <p className="text-xl font-bold text-blue-600">{detail.data.total_nights}</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-purple-700 font-semibold">Avg spend</p>
                    <p className="text-xl font-bold text-purple-600">{eur(detail.data.avg_spend)}</p>
                  </div>
                </div>

                {detail.data.favourite_room && (
                  <div className="bg-white border rounded-lg p-3 flex items-center gap-3" data-testid="loyalty-drawer-fav-room">
                    <Bed className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Favourite room</p>
                      <p className="text-sm font-bold text-gray-900">{detail.data.favourite_room}</p>
                    </div>
                  </div>
                )}
                {detail.data.suggested_next_visit && (
                  <div className="bg-white border rounded-lg p-3 flex items-center gap-3" data-testid="loyalty-drawer-next">
                    <TrendingUp className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Next visit likely around</p>
                      <p className="text-sm font-bold text-gray-900">
                        {new Date(detail.data.suggested_next_visit).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        <span className="text-xs text-gray-500 ml-2">(avg gap {detail.data.avg_gap_days}d)</span>
                      </p>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Past bookings</p>
                  <div className="space-y-2">
                    {detail.data.bookings.map((b, i) => (
                      <div key={i} className="border rounded-lg p-3 text-sm" data-testid={`loyalty-drawer-booking-${i}`}>
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{b.room_name}</p>
                            <p className="text-xs text-gray-500">{b.check_in} → {b.check_out} · {b.nights}n · {b.source || 'direct'}</p>
                          </div>
                          <p className="text-sm font-bold text-orange-600 flex-shrink-0">{eur(b.revenue)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyWallOfFame;
